import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTheme } from "../../contexts/ThemeContext";
import {
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  X,
} from "lucide-react";
import AppButton from "../shared/AppButton.jsx";

const navItems = [
  {
    path: "/whatsappdashboard",
    label: "Dashboard",
    icon: "fa-tachometer-alt",
  },
  {
    path: "/create-new",
    label: "Compose",
    icon: "fa-pen-to-square",
  },
  { path: "/whatsapp-message", label: "History", icon: "fa-history" },
  { path: "/sent-message", label: "In Process", icon: "fa-rotate-right" },
  { path: "/schedule", label: "Scheduler", icon: "fa-calendar" },
  {
    path: "/Birthday/schedule",
    label: "B'day Scheduler",
    icon: "fa-birthday-cake",
  },
  { path: "/template", label: "Create Template", icon: "fa-file-alt" },
  { path: "/whatsapp-groups-details", label: "WA Groups", icon: "fa-users" },
  { path: "/settings", label: "Settings", icon: "fa-user" },
];

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const { theme } = useTheme();
  const reduceMotion = useReducedMotion();
  const impersonationMeta = window.__appSession.getImpersonationMeta();
  const isImpersonating = window.__appSession.isImpersonating();

  const handleLogout = async () => {
    if (isImpersonating) {
      window.__appSession.endImpersonationSession();
      navigate("/admin");
      setIsSidebarOpen(false);
      return;
    }

    const token = window.__appSession.getToken();
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        window.__appSession.clearAllSessions();
        localStorage.removeItem("selectedTheme");
        navigate("/");
      } else {
        const errorData = await response.json();
        console.error("Logout error:", errorData.message);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsSidebarOpen(false);
  };

  return (
    <div className={`relative flex min-h-screen flex-col overflow-hidden ${theme.mainBg}`}>
      <div
        className="aurora-dot h-48 w-48 bg-indigo-300"
        style={{ top: "4rem", left: "-3rem" }}
      />
      <div
        className="aurora-dot h-64 w-64 bg-violet-300"
        style={{ top: "32%", right: "-5rem", animationDelay: "1.4s" }}
      />

      <header className="fixed inset-x-0 top-0 z-40 px-1.5 pt-1 sm:px-3 sm:pt-2">
        <motion.div
          className={`glass-surface-strong ${theme.headerBorder} ${theme.headerText} flex items-center justify-between gap-3 rounded-2xl border px-3 py-2 sm:px-3.5`}
          initial={reduceMotion ? false : { y: -14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35 }}
        >
          <Link to="/whatsappdashboard" className="flex items-center gap-3 rounded-xl">
            <motion.img
              src="/logo.png"
              alt="Logo"
              className="h-10 w-auto object-contain sm:h-11"
              whileHover={reduceMotion ? undefined : { rotate: -4, scale: 1.03 }}
              transition={{ type: "spring", stiffness: 300, damping: 18 }}
            />
            <div className="hidden sm:block leading-tight">
              <p className="text-sm font-semibold text-slate-800">Smart ITBox</p>
              <p className="text-[11px] text-slate-500">Automation Workspace</p>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <div className="hidden lg:flex items-center gap-2 rounded-xl border border-indigo-200 bg-white/70 px-2.5 py-1 text-[11px] text-slate-700">
              <Sparkles className="h-3.5 w-3.5 text-violet-600" />
              <span className="font-semibold">
                {isImpersonating ? "Impersonation Workspace" : "User Workspace"}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Live
              </span>
            </div>

            <button
              onClick={() => setIsCompact((prev) => !prev)}
              className="hidden lg:inline-flex rounded-xl border border-indigo-200 bg-white/80 p-2 text-slate-700 transition hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
              aria-label="Toggle compact sidebar"
            >
              {isCompact ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>

            <AppButton
              variant="danger"
              size="sm"
              onClick={handleLogout}
              className="hidden lg:inline-flex"
            >
              <LogOut size={15} className="mr-2" />
              {isImpersonating ? "Exit Impersonation" : "Logout"}
            </AppButton>

            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden rounded-xl border border-indigo-200 bg-white/80 p-2 text-slate-700 transition hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
              aria-label="Open sidebar"
            >
              <Menu size={20} />
            </button>
          </div>
        </motion.div>
      </header>

      <div className="flex flex-1 pt-[76px] sm:pt-[84px]">
        <motion.aside
          className="fixed inset-y-0 left-0 z-30 hidden px-1.5 pb-3 pt-[82px] lg:flex"
          animate={{ width: isCompact ? 96 : 248 }}
          transition={
            reduceMotion
              ? { duration: 0.01 }
              : { type: "spring", stiffness: 230, damping: 26 }
          }
        >
          <div
            className={`glass-surface ${theme.sidebarBg} ${theme.sidebarText} ${theme.sidebarBorder} flex h-full w-full flex-col rounded-2xl border p-2`}
          >
            <nav className="custom-scrollbar flex-1 overflow-y-auto space-y-1">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.path;

                return (
                  <motion.div
                    key={item.path}
                    initial={reduceMotion ? false : { opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.22, delay: index * 0.03 }}
                  >
                    <Link
                      to={item.path}
                      title={item.label}
                      aria-current={isActive ? "page" : undefined}
                      className={`group flex items-center rounded-xl border px-2.5 py-2 transition-all ${
                        isActive
                          ? "border-indigo-300/90 bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-800 shadow-[0_12px_22px_-18px_rgba(79,70,229,0.42)]"
                          : `border-transparent ${theme.sidebarHover} ${theme.sidebarText} hover:border-indigo-200`
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs ${
                          isActive
                            ? "border border-indigo-200 bg-white text-indigo-700"
                            : "bg-white text-indigo-600 border border-indigo-100"
                        }`}
                      >
                        <i className={`fa ${item.icon}`} />
                      </span>
                      <span
                        className={`ml-2 whitespace-nowrap text-sm font-semibold transition-opacity ${
                          isCompact ? "opacity-0 pointer-events-none w-0" : "opacity-100"
                        }`}
                      >
                        {item.label}
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </nav>

            <div className="mt-2 rounded-xl border border-indigo-200 bg-white/75 p-2 text-[11px] text-slate-600">
              <p className="font-semibold text-slate-800">Need help?</p>
              <p className="mt-0.5">Reach the team from profile support options.</p>
            </div>
          </div>
        </motion.aside>

        <AnimatePresence>
          {isSidebarOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-40 bg-slate-900/45 lg:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsSidebarOpen(false)}
              />

              <motion.aside
                className={`fixed inset-y-0 left-0 z-50 w-72 p-3 lg:hidden`}
                initial={reduceMotion ? false : { x: -320, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={reduceMotion ? { x: 0, opacity: 0 } : { x: -320, opacity: 0 }}
                transition={{ type: "spring", stiffness: 240, damping: 26 }}
              >
                <div
                  className={`glass-surface-strong ${theme.sidebarBg} ${theme.sidebarText} ${theme.sidebarBorder} flex h-full flex-col rounded-2xl border p-3`}
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">Navigation</span>
                    <button
                      onClick={() => setIsSidebarOpen(false)}
                      className="rounded-lg border border-indigo-200 p-1.5 text-slate-600 hover:bg-indigo-50"
                      aria-label="Close sidebar"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <nav className="space-y-1">
                    {navItems.map((item, index) => {
                      const isActive = location.pathname === item.path;

                      return (
                        <motion.div
                          key={item.path}
                          initial={reduceMotion ? false : { opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.03 }}
                        >
                          <Link
                            to={item.path}
                            onClick={() => setIsSidebarOpen(false)}
                            aria-current={isActive ? "page" : undefined}
                            className={`flex items-center rounded-xl border px-3 py-2.5 text-sm font-semibold transition-all ${
                              isActive
                                ? "border-indigo-300/90 bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-800"
                                : "border-transparent text-slate-700 hover:border-indigo-200 hover:bg-indigo-50"
                            }`}
                          >
                            <span
                              className={`mr-2.5 flex h-7 w-7 items-center justify-center rounded-lg text-[11px] ${
                                isActive
                                  ? "border border-indigo-200 bg-white text-indigo-700"
                                  : "bg-white text-indigo-600 border border-indigo-100"
                              }`}
                            >
                              <i className={`fa ${item.icon}`} />
                            </span>
                            {item.label}
                          </Link>
                        </motion.div>
                      );
                    })}
                  </nav>

                  <AppButton
                    variant="danger"
                    size="sm"
                    onClick={handleLogout}
                    className="mt-auto w-full"
                  >
                    <LogOut size={15} className="mr-2" />
                    {isImpersonating ? "Exit Impersonation" : "Logout"}
                  </AppButton>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <main
          className={`flex-1 w-full overflow-y-auto px-1.5 pb-2 pt-0.5 transition-all duration-300 sm:px-3 sm:pb-3 ${
            isCompact ? "lg:ml-[96px]" : "lg:ml-[248px]"
          }`}
        >
          {isImpersonating && (
            <div className="mb-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold">
                    Impersonating {impersonationMeta?.impersonatedUser?.name || "user"}
                  </p>
                  <p className="mt-0.5 text-amber-800">
                    Started by {impersonationMeta?.impersonator?.name || "admin"} and valid until{" "}
                    {impersonationMeta?.expiresAt
                      ? new Date(impersonationMeta.expiresAt).toLocaleString()
                      : "session expiry"}
                    .
                  </p>
                </div>
                <AppButton variant="secondary" size="sm" onClick={handleLogout}>
                  Exit Impersonation
                </AppButton>
              </div>
            </div>
          )}

          <motion.div
            className="glass-surface min-h-[calc(100vh-142px)] rounded-2xl border border-indigo-200/60 p-1 sm:p-2"
            initial={reduceMotion ? false : { y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.28 }}
          >
            {children}
          </motion.div>
        </main>
      </div>

      <footer className="px-2 pb-3 sm:px-4 sm:pb-4">
        <div className="rounded-2xl border border-gray-300 bg-gray-200 px-3 py-2 text-center text-[11px] text-black shadow-sm sm:text-right">
          Designed and crafted by <span className="font-semibold text-yellow-500">Smart Itbox</span> | Your Business Automation Partner
        </div>
      </footer>
    </div>
  );
};

export default Layout;
